import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, OnInit, Output } from '@angular/core';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { PaginationInfo, PaginatedResponse } from 'src/app/shared/models';
import { PurchaseManagerService } from 'src/app/services/purchase-manager/purchase-manager.service';
import { PurchaseManagerModalService } from 'src/app/services/purchase-manager-modal/purchase-manager-modal.service';
import Swal from 'sweetalert2';

declare var bootstrap: any;
declare var KTMenu: any;

@Component({
  selector: 'app-purchase-manager-listing',
  templateUrl: './purchase-manager-listing.component.html',
  styleUrls: ['./purchase-manager-listing.component.scss']
})
export class PurchaseManagerListingComponent extends DestructorComponent implements OnInit, AfterViewInit {

  @Output() purchaseManagerAdded = new EventEmitter<void>();
  @Output() purchaseManagerUpdated = new EventEmitter<void>();

  // Data properties
  purchaseManagers: any[] = [];
  paginationInfo: PaginationInfo = {
    currentPage: 1,
    perPage: 20,
    total: 0,
    lastPage: 1,
    firstPage: 1
  };

  // State properties
  isLoading: boolean = false;
  searchTerm: string = '';
  currentPage: number = 1;
  showFiltersForm = false;

  // Filter properties
  filters = {
    search: '',
    date_from: '',
    date_to: '',
    location: '',
    vendor_id: ''
  };

  constructor(
    private _purchaseManagerService: PurchaseManagerService,
    private _purchaseManagerModalService: PurchaseManagerModalService,
    private cdr: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit(): void {
    this.loadPurchaseManagers();
  }

  ngAfterViewInit(): void {
    // Initialize menus after view is initialized
    this.initializeMenus();
  }

  private initializeMenus(): void {
    setTimeout(() => {
      KTMenu.createInstances();
    }, 100);
  }

  private refreshMenus(): void {
    setTimeout(() => {
      KTMenu.createInstances();
    }, 100);
  }

  loadPurchaseManagers(page: number = 1, search: string = '', filters: Record<string, any> = {}): void {
    this.isLoading = true;
    this.currentPage = page;

    delete filters.search;

    this.subscriptions.push(
      this._purchaseManagerService.GetData(page.toString(), search, filters).subscribe({
        next: (response: PaginatedResponse<any>) => {
          if (response.status && response.data) {
            this.purchaseManagers = response.data.data || [];
            this.paginationInfo = response.data.pagination;
            this.currentPage = response.data.pagination.currentPage;
          } else {
            this.purchaseManagers = [];
            this.resetPagination();
          }
          this.isLoading = false;
          this.cdr.detectChanges();
          this.refreshMenus();
        },
        error: (error) => {
          console.error('Error loading purchase managers:', error);
          this.purchaseManagers = [];
          this.resetPagination();
          this.isLoading = false;
          this.cdr.detectChanges();
          this.refreshMenus();
        }
      })
    );
  }

  onPageChange(page: number): void {
    this.loadPurchaseManagers(page, this.searchTerm, this.filters);
  }

  toggleFilters(): void {
    this.showFiltersForm = !this.showFiltersForm;
  }

  applyFilters(): void {
    this.filters.search = this.searchTerm;
    this.loadPurchaseManagers(1, this.searchTerm, this.filters);
  }

  clearFilters(): void {
    this.filters = {
      search: '',
      date_from: '',
      date_to: '',
      location: '',
      vendor_id: ''
    };
    this.searchTerm = '';
    this.loadPurchaseManagers(1, '', {});
  }

  openAddPurchaseManagerModal(): void {
    this._purchaseManagerModalService.clearSelectedPurchaseManager();
    const modalElement = document.getElementById('kt_modal_purchase_manager_add_edit');
    if (modalElement) {
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  openEditPurchaseManagerModal(purchaseManager: any): void {
    this._purchaseManagerModalService.setSelectedPurchaseManager(purchaseManager);
    const modalElement = document.getElementById('kt_modal_purchase_manager_add_edit');
    if (modalElement) {
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  openUploadDocsModal(purchaseManager: any): void {
    (window as any).selectedPurchaseManagerForDocs = purchaseManager;
    const modalElement = document.getElementById('kt_modal_purchase_manager_upload_docs');
    if (modalElement) {
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  onDocumentsUploaded(): void {
    console.log('Documents uploaded, refreshing list...');
    this.loadPurchaseManagers(this.currentPage, this.searchTerm, this.filters);
  }

  deletePurchaseManager(purchaseManager: any): void {
    Swal.fire({
      title: 'Are you sure?',
      text: `You want to delete this purchase manager record?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    }).then((result) => {
      if (result.isConfirmed) {
        this.performDelete(purchaseManager);
      }
    });
  }

  private performDelete(purchaseManager: any): void {
    const purchaseManagerId = purchaseManager._id || purchaseManager.id;
    this.subscriptions.push(
      this._purchaseManagerService.DeleteDataById(purchaseManagerId).subscribe({
        next: (response: any) => {
          if (response.status) {
            Swal.fire({
              title: 'Deleted!',
              text: 'Purchase manager record has been deleted successfully.',
              icon: 'success',
              confirmButtonText: 'OK'
            }).then(() => {
              this.loadPurchaseManagers(this.currentPage, this.searchTerm, this.filters);
            });
          } else {
            Swal.fire({
              title: 'Error!',
              text: response.message || 'Failed to delete purchase manager record.',
              icon: 'error',
              confirmButtonText: 'OK'
            });
          }
        },
        error: (error: any) => {
          console.error('Error deleting purchase manager:', error);
          Swal.fire({
            title: 'Error!',
            text: 'Failed to delete purchase manager record. Please try again.',
            icon: 'error',
            confirmButtonText: 'OK'
          });
        }
      })
    );
  }

  onPurchaseManagerAdded(): void {
    console.log('Purchase manager added, refreshing list...');
    this.loadPurchaseManagers(this.currentPage, this.searchTerm, this.filters);
    this.purchaseManagerAdded.emit();
  }

  onPurchaseManagerUpdated(): void {
    console.log('Purchase manager updated, refreshing list...');
    this.loadPurchaseManagers(this.currentPage, this.searchTerm, this.filters);
    this.purchaseManagerUpdated.emit();
  }

  private resetPagination(): void {
    this.paginationInfo = {
      currentPage: 1,
      perPage: 20,
      total: 0,
      lastPage: 1,
      firstPage: 1
    };
  }

  trackByFn(index: number, item: any): any {
    return item._id || item.id || index;
  }

  // Utility methods for display
  getVehicleDisplayName(vehicle: any): string {
    if (!vehicle) return '-';
    const parts = [];
    if (vehicle.name) parts.push(vehicle.name);
    if (vehicle.company_name) parts.push(vehicle.company_name);
    if (vehicle.year) parts.push(`(${vehicle.year})`);
    return parts.join(' ') || 'Unknown Vehicle';
  }

  getVendorDisplayName(vendor: any): string {
    if (!vendor) return '-';
    return vendor.company_name || vendor.name || 'Unknown Vendor';
  }

  formatAmount(amount: any): string {
    if (!amount) return '-';
    return new Intl.NumberFormat('ja-JP', {
      style: 'currency',
      currency: 'JPY',
      minimumFractionDigits: 0
    }).format(amount);
  }

  formatDate(date: any): string {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  openFile(fileUrl: string): void {
    if (fileUrl) {
      window.open(fileUrl, '_blank');
    }
  }
}
